/**
 * Remote matching API (pairs).
 * (session assignments + core pairs). Mirrors matching.controller.ts —
 * docs/spec-matching-interface.md Part F §29. Do not invent query params
 * or scores the backend does not return.
 */

import { apiClient } from '@/services/api-client';
import type {
  TutorDashboardItem,
  StudentDashboardItem,
  DashboardResponse,
  MatchedStudentItem,
  MatchedTutorItem,
  UnmatchedStudentItem,
  UnmatchedTutorItem,
  CheckAvailabilityRequest,
  CheckAvailabilityResponse,
  TutorDashboardParams,
  StudentDashboardParams,
  TutorMatchedStudentsParams,
  TutorUnmatchedStudentsParams,
  StudentMatchedTutorParams,
  StudentUnmatchedTutorsParams,
} from '@/types/remote/matching.types';

/** Skip empty query values when building matching dashboard URLs. */
function appendQueryValue(
  params: URLSearchParams,
  key: string,
  value: string | number | boolean | undefined | null,
): void {
  if (value === undefined || value === null || value === '') return;
  params.append(key, String(value));
}

/** Flatten {@link TutorDashboardParams} onto `GET .../matching/tutor/dashboard`. */
function appendTutorDashboardParams(
  searchParams: URLSearchParams,
  params?: TutorDashboardParams,
): void {
  appendQueryValue(searchParams, 'page', params?.page);
  appendQueryValue(searchParams, 'pageSize', params?.pageSize);
  appendQueryValue(searchParams, 'status', params?.status);
  appendQueryValue(searchParams, 'tutor', params?.tutor);
  appendQueryValue(searchParams, 'gender', params?.gender);
  if (params?.programs?.length) {
    params.programs.forEach((id) => searchParams.append('programs', id));
  } else {
    appendQueryValue(searchParams, 'program', params?.program);
  }
  appendQueryValue(searchParams, 'subject', params?.subject);
  appendQueryValue(searchParams, 'establishment', params?.establishment);
  appendQueryValue(searchParams, 'numberOfStudentsToSupport', params?.numberOfStudentsToSupport);
  appendQueryValue(
    searchParams,
    'leftNumberOfStudentsToSupport',
    params?.leftNumberOfStudentsToSupport,
  );
  appendQueryValue(searchParams, 'partner', params?.partner);
  appendQueryValue(searchParams, 'schoolYearId', params?.schoolYearId);
  appendQueryValue(searchParams, 'cjValidatedFrom', params?.cjValidatedFrom);
  appendQueryValue(searchParams, 'cjValidatedTo', params?.cjValidatedTo);
  appendQueryValue(searchParams, 'participationStudies', params?.participationStudies);
  appendQueryValue(searchParams, 'participationRI', params?.participationRI);
  appendQueryValue(searchParams, 'registrationDateFrom', params?.registrationDateFrom);
  appendQueryValue(searchParams, 'registrationDateTo', params?.registrationDateTo);
  appendQueryValue(searchParams, 'partnerId', params?.partnerId);
  appendQueryValue(searchParams, 'valorisation', params?.valorisation);
  params?.level?.forEach((id) => searchParams.append('level', id));
  appendQueryValue(searchParams, 'affectability', params?.affectability);
  if (params?.exportCsv) searchParams.append('exportCsv', 'true');
}

/** `GET /companies/:companyId/matching/tutor/dashboard` — paginated tutor matching table. */
export async function getTutorDashboard(
  companyId: string,
  params?: TutorDashboardParams,
): Promise<DashboardResponse<TutorDashboardItem>> {
  const searchParams = new URLSearchParams();
  appendTutorDashboardParams(searchParams, params);

  const response = await apiClient.get<DashboardResponse<TutorDashboardItem>>(
    `/companies/${companyId}/matching/tutor/dashboard?${searchParams.toString()}`,
  );
  return response.data;
}

/**
 * GET /companies/:companyId/matching/student/dashboard
 *
 * When to call: student view of the main table.
 * Same logic as tutor dashboard (without level/leftNumberOfStudentsToSupport params).
 */
export async function getStudentDashboard(
  companyId: string,
  params?: StudentDashboardParams,
): Promise<DashboardResponse<StudentDashboardItem>> {
  const searchParams = new URLSearchParams();
  // Same query shape as the tutor dashboard, minus level[] / leftover-capacity params.

  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.append('status', params.status);
  if (params?.student) searchParams.append('student', params.student);
  if (params?.gender) searchParams.append('gender', params.gender);
  if (params?.program) searchParams.append('program', params.program);
  if (params?.subject) searchParams.append('subject', params.subject);
  if (params?.establishment) searchParams.append('establishment', params.establishment);
  if (params?.partner !== undefined) searchParams.append('partner', params.partner.toString());
  if (params?.schoolYearId) searchParams.append('schoolYearId', params.schoolYearId);
  if (params?.partnerId) searchParams.append('partnerId', params.partnerId);
  if (params?.level) searchParams.append('level', params.level);
  if (params?.linkedUserId) searchParams.append('linkedUserId', params.linkedUserId);
  if (params?.affectability !== undefined)
    searchParams.append('affectability', String(params.affectability));
  if (params?.exportCsv) searchParams.append('exportCsv', 'true');

  const response = await apiClient.get<DashboardResponse<StudentDashboardItem>>(
    `/companies/${companyId}/matching/student/dashboard?${searchParams.toString()}`,
  );
  return response.data;
}

/**
 * GET /companies/:companyId/matching/tutor/students-matched
 *
 * When to call: when expanding a tutor row (column 2 — already matched students).
 * Optional: filter by tutorAvailability (JSON string) to show only students matched on that slot.
 */
export async function getTutorStudentsMatched(
  companyId: string,
  params: TutorMatchedStudentsParams,
): Promise<MatchedStudentItem[]> {
  const searchParams = new URLSearchParams();
  searchParams.append('userId', params.userId);
  if (params.tutorAvailability) {
    searchParams.append('tutorAvailability', params.tutorAvailability);
  }

  const response = await apiClient.get<MatchedStudentItem[]>(
    `/companies/${companyId}/matching/tutor/students-matched?${searchParams.toString()}`,
  );
  return response.data;
}

/**
 * GET /companies/:companyId/matching/tutor/students-unmatched
 *
 * KEY ENDPOINT: "which students can be matched with this tutor?"
 * When to call: when expanding a tutor row, column 3 (candidates to add),
 * typically after selecting a tutor's slot or entering a subject/name/gender filter.
 *
 * LIMIT: hard limit of 200 results, no pagination.
 * IMPORTANT: No compatibility score, no non-compatibility reasons returned (§29.5 note).
 */
export async function getTutorStudentsUnmatched(
  companyId: string,
  params: TutorUnmatchedStudentsParams,
): Promise<DashboardResponse<UnmatchedStudentItem>> {
  const searchParams = new URLSearchParams();
  searchParams.append('userId', params.userId);
  if (params.tutorAvailability) {
    searchParams.append('tutorAvailability', params.tutorAvailability);
  }
  if (params.subject) searchParams.append('subject', params.subject);
  if (params.studentName) searchParams.append('studentName', params.studentName);
  if (params.gender) searchParams.append('gender', params.gender);
  if (params.page != null) searchParams.append('page', String(params.page));
  if (params.pageSize != null) searchParams.append('pageSize', String(params.pageSize));

  const response = await apiClient.get<DashboardResponse<UnmatchedStudentItem>>(
    `/companies/${companyId}/matching/tutor/students-unmatched?${searchParams.toString()}`,
  );
  return response.data;
}

/**
 * GET /companies/:companyId/matching/student/tutor-matched
 *
 * Symmetric of §29.4 for students: userId = student UUID, studentAvailability optional.
 * Returns tutor(s) already in active binome with this student.
 */
export async function getStudentTutorMatched(
  companyId: string,
  params: StudentMatchedTutorParams,
): Promise<MatchedTutorItem[]> {
  const searchParams = new URLSearchParams();
  searchParams.append('userId', params.userId);
  if (params.studentAvailability) {
    searchParams.append('studentAvailability', params.studentAvailability);
  }

  const response = await apiClient.get<MatchedTutorItem[]>(
    `/companies/${companyId}/matching/student/tutor-matched?${searchParams.toString()}`,
  );
  return response.data;
}

/**
 * GET /companies/:companyId/matching/student/tutors-unmatched
 *
 * Symmetric of §29.5 for students: compatible tutors for this student.
 * LIMIT: max 200 results, all with leftNumberOfStudentsToSupport > 0 (automatic filter).
 */
export async function getStudentTutorsUnmatched(
  companyId: string,
  params: StudentUnmatchedTutorsParams,
): Promise<DashboardResponse<UnmatchedTutorItem>> {
  const searchParams = new URLSearchParams();
  searchParams.append('userId', params.userId);
  if (params.studentAvailability) {
    searchParams.append('studentAvailability', params.studentAvailability);
  }
  if (params.subject) searchParams.append('subject', params.subject);
  if (params.level) searchParams.append('level', params.level);
  if (params.tutorName) searchParams.append('tutorName', params.tutorName);
  if (params.gender) searchParams.append('gender', params.gender);
  if (params.page != null) searchParams.append('page', String(params.page));
  if (params.pageSize != null) searchParams.append('pageSize', String(params.pageSize));

  const response = await apiClient.get<DashboardResponse<UnmatchedTutorItem>>(
    `/companies/${companyId}/matching/student/tutors-unmatched?${searchParams.toString()}`,
  );
  return response.data;
}

/**
 * POST /companies/:companyId/matching/check-availability
 *
 * When to call: during manual matching form input, to visually indicate (e.g., gray out)
 * which declared slots of the current user are already "taken" by an existing binome partner,
 * before even selecting a specific candidate.
 *
 * Body: { tutor: boolean, user: { userId, programId, availability[] } }
 * Returns: { data: [{ day, slot, taken }] }
 *
 * NOTE: programId is required in the DTO but unused in backend query (§29.8 bug note).
 */
export async function checkAvailability(
  companyId: string,
  body: CheckAvailabilityRequest,
): Promise<CheckAvailabilityResponse> {
  const response = await apiClient.post<CheckAvailabilityResponse>(
    `/companies/${companyId}/matching/check-availability`,
    body,
  );
  return response.data;
}
